import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Facebook, Instagram, Mail, Phone, MapPin } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const { t, language } = useTranslation();
  const { localePath: lp } = useLanguage();
  const [socialLinks, setSocialLinks] = useState({ facebook: '', instagram: '' });
  const [contactInfo, setContactInfo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [socialRes, contactRes] = await Promise.all([
          axios.get(`${API}/api/social`),
          axios.get(`${API}/api/contact-info`)
        ]);
        setSocialLinks(socialRes.data);
        setContactInfo(contactRes.data);
      } catch (error) {
        console.error('Error fetching footer data:', error);
      }
    };
    fetchData();
  }, []);
  
  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center">
              <img 
                src="/rete-italia-logo.png" 
                alt="Rete Italia" 
                className="h-12 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm">
              {t('footer.description')}
            </p>
            {/* Flag Colors Accent */}
            <div className="flex gap-2">
              <div className="w-8 h-2 bg-[#009246] rounded"></div>
              <div className="w-8 h-2 bg-white rounded"></div>
              <div className="w-8 h-2 bg-[#CE2B37] rounded"></div>
              <div className="w-8 h-2 bg-[#00247D] rounded"></div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li><Link to={lp('/')} className="text-gray-400 hover:text-white transition-colors">{t('header.home')}</Link></li>
              <li><Link to={lp('/chi-siamo')} className="text-gray-400 hover:text-white transition-colors">{t('header.about')}</Link></li>
              <li><Link to={lp('/palinsesto')} className="text-gray-400 hover:text-white transition-colors">{t('header.schedule')}</Link></li>
              <li><Link to={lp('/podcast')} className="text-gray-400 hover:text-white transition-colors">{t('header.podcasts')}</Link></li>
              <li><Link to={lp('/contatti')} className="text-gray-400 hover:text-white transition-colors">{t('header.contact')}</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.contact')}</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-gray-400 text-sm">
                <Mail className="w-4 h-4 text-[#009246]" />
                <span>{contactInfo?.email || 'info@reteitalia.com.au'}</span>
              </li>
              <li className="flex items-center gap-2 text-gray-400 text-sm">
                <Phone className="w-4 h-4 text-[#CE2B37]" />
                <span>{contactInfo?.phone || '+61 3 9481 0666'}</span>
              </li>
              <li className="flex items-start gap-2 text-gray-400 text-sm">
                <MapPin className="w-4 h-4 text-[#00247D] mt-1" />
                <span>Melbourne<br />{contactInfo?.melbourneAddress || '35-37 Melville Road, Brunswick West VIC 3055, Australia'}</span>
              </li>
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.followUs')}</h3>
            <div className="flex gap-3">
              <a
                href={socialLinks.facebook || 'https://www.facebook.com/reteitalia'}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-[#1877F2] rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={socialLinks.instagram || 'https://www.instagram.com/reteitaliaaustralia/'}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#FD1D1D] hover:to-[#F77737] rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400 text-sm">
          <p>&copy; {new Date().getFullYear()} Niche Radio Network Pty Ltd. {t('footer.allRightsReserved')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
